import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';

export type PushSupportState = 'loading' | 'unsupported' | 'denied' | 'default' | 'granted';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export function usePushNotifications() {
  const [support, setSupport] = useState<PushSupportState>('loading');
  const [subscriptionEndpoint, setSubscriptionEndpoint] = useState<string | null>(null);
  const [vapidConfigured, setVapidConfigured] = useState(true);

  const refresh = useCallback(async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupport('unsupported');
      return;
    }

    try {
      await api<{ publicKey: string }>('/notifications/vapid-public-key');
      setVapidConfigured(true);
    } catch {
      setVapidConfigured(false);
    }

    const permission = Notification.permission;
    if (permission === 'denied') {
      setSupport('denied');
      return;
    }
    if (permission === 'granted') {
      setSupport('granted');
    } else {
      setSupport('default');
    }

    try {
      const reg = await getServiceWorkerRegistration();
      const sub = await reg?.pushManager.getSubscription();
      setSubscriptionEndpoint(sub?.endpoint ?? null);
    } catch {
      setSubscriptionEndpoint(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!vapidConfigured) throw new Error('push_not_configured');

    const { publicKey } = await api<{ publicKey: string }>('/notifications/vapid-public-key');
    const reg = await getServiceWorkerRegistration();
    if (!reg) throw new Error('no_service_worker');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      setSupport('denied');
      throw new Error('permission_denied');
    }

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const json = sub.toJSON();
    await api('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: json.keys,
      }),
    });

    setSubscriptionEndpoint(sub.endpoint);
    setSupport('granted');
    return sub.endpoint;
  }, [vapidConfigured]);

  const unsubscribe = useCallback(async () => {
    const reg = await getServiceWorkerRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) {
      setSubscriptionEndpoint(null);
      return;
    }

    const endpoint = sub.endpoint;
    await api('/notifications/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
    await sub.unsubscribe();
    setSubscriptionEndpoint(null);
  }, []);

  return {
    support,
    vapidConfigured,
    subscriptionEndpoint,
    refresh,
    subscribe,
    unsubscribe,
    isSubscribed: !!subscriptionEndpoint,
  };
}

export const NOTIFY_HOUR_OPTIONS = Array.from({ length: 15 }, (_, i) => {
  const hour = i + 7;
  return `${String(hour).padStart(2, '0')}:00`;
});
